import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('sgfi_token'));
  const [loading, setLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      // First check the user's status without being blocked
      const statusResponse = await axios.get(`${API_URL}/auth/status`);
      const statusData = statusResponse.data;
      
      // If user is pending, set the pending flag and return minimal user data
      if (statusData.status === 'pending') {
        setIsPending(true);
        setUser({
          id: statusData.id,
          email: statusData.email,
          name: statusData.name,
          status: statusData.status,
          is_admin: statusData.is_admin
        });
        setLoading(false);
        return;
      }
      
      // If user is active, fetch full user data
      setIsPending(false);
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('sgfi_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    
    // Check if user is pending
    if (userData.status === 'pending') {
      setIsPending(true);
      setUser(userData);
    } else {
      setIsPending(false);
      setUser(userData);
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('sgfi_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsPending(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, isPending, login, logout, updateUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
